import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  LocalShipping,
  Inventory,
  LocationOn,
  CalendarToday,
  TrendingUp,
  OpenInFull
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';

const ChartCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5)
    }
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

// Scrollable chart container
const ScrollableChartBox = styled(Box)(({ theme }) => ({
  height: 320,
  overflow: 'auto',
  padding: theme.spacing(0.5),
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#555'
    }
  }
}));

const PalletScoreCard = ({ shipments, setExpandedChart }) => {
  const [sortConfig, setSortConfig] = useState({
    field: 'deliveryDate',
    direction: 'desc'
  });

  console.log(shipments, 'shipments');

  // Calculate statistics using all shipments (no date filtering)
  const statistics = useMemo(() => {
    // Use all shipments (assuming they're already filtered for delivered status if needed)
    const deliveredShipments = shipments;

    const totalPallets = deliveredShipments.reduce(
      (sum, s) => sum + (Number(s.totalUnits) || 0),
      0
    );

    const totalShipments = deliveredShipments.length;

    const uniqueCarriers = new Set(
      deliveredShipments.map(s => s.carrierName).filter(Boolean)
    ).size;

    const avgPalletsPerShipment =
      totalShipments > 0 ? (totalPallets / totalShipments).toFixed(1) : 0;

    // Calculate pallets by carrier (all carriers, no limits)
    const palletsByCarrier = {};
    const shipmentsByCarrier = {};

    deliveredShipments.forEach(shipment => {
      const carrier = shipment.carrierName || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByCarrier[carrier]) {
        palletsByCarrier[carrier] = 0;
        shipmentsByCarrier[carrier] = 0;
      }

      palletsByCarrier[carrier] += pallets;
      shipmentsByCarrier[carrier] += 1;
    });

    // Calculate pallets by location (origin city) - all locations
    const palletsByLocation = {};
    const shipmentsByLocation = {};

    deliveredShipments.forEach(shipment => {
      const location = shipment.originCity || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByLocation[location]) {
        palletsByLocation[location] = 0;
        shipmentsByLocation[location] = 0;
      }

      palletsByLocation[location] += pallets;
      shipmentsByLocation[location] += 1;
    });

    // Calculate daily pallet deliveries (all dates, no limits)
    const dailyPallets = {};

    deliveredShipments.forEach(shipment => {
      if (shipment.actualDeliveryDate) {
        const date = moment(shipment.actualDeliveryDate).format('YYYY-MM-DD');
        const pallets = Number(shipment.totalUnits) || 0;

        if (!dailyPallets[date]) {
          dailyPallets[date] = 0;
        }

        dailyPallets[date] += pallets;
      }
    });

    // Get all unique dates and sort them chronologically
    const allDates = Object.keys(dailyPallets).sort();

    // Format dates for chart display
    const chartDates = allDates.map(date => moment(date).format('MMM DD YYYY'));
    const chartData = allDates.map(date => dailyPallets[date] || 0);

    // Get all carriers sorted by pallets (no slicing)
    const allCarriers = Object.entries(palletsByCarrier)
      .sort(([, a], [, b]) => b - a)
      .map(([carrier, pallets]) => ({
        carrier,
        pallets,
        shipments: shipmentsByCarrier[carrier] || 0
      }));

    // Get all locations sorted by pallets (no slicing)
    const allLocations = Object.entries(palletsByLocation)
      .sort(([, a], [, b]) => b - a)
      .map(([location, pallets]) => ({
        location,
        pallets,
        shipments: shipmentsByLocation[location] || 0
      }));

    return {
      totalPallets,
      totalShipments,
      uniqueCarriers,
      avgPalletsPerShipment,
      allCarriers, // All carriers, no limit
      allLocations, // All locations, no limit
      dailyChartData: {
        dates: chartDates,
        pallets: chartData
      },
      // For the carrier chart - all carriers
      allCarriersData: {
        carriers: Object.keys(palletsByCarrier),
        pallets: Object.values(palletsByCarrier)
      },
      // For the location chart - all locations
      allLocationsData: {
        locations: Object.keys(palletsByLocation),
        pallets: Object.values(palletsByLocation)
      }
    };
  }, [shipments]);

  // Handle sort for table
  const handleSort = field => {
    setSortConfig(prev => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted pallet delivery data for table (all deliveries, no filtering)
  const getSortedPalletDeliveries = () => {
    const deliveries = shipments
      .filter(s => Number(s.totalUnits) > 0)
      .map(s => ({
        id: s.id,
        deliveryDate: s.actualDeliveryDate,
        carrier: s.carrierName || 'Unknown',
        originLocation:
          `${s.originCity || ''}, ${s.originState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        pallets: Number(s.totalUnits) || 0,
        destination:
          `${s.destinationCity || ''}, ${s.destinationState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        proNumber: s.proNumber || '-',
        bolNumber: s.bolNumber || '-'
      }));

    return deliveries.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case 'deliveryDate':
          aValue = a.deliveryDate ? new Date(a.deliveryDate) : new Date(0);
          bValue = b.deliveryDate ? new Date(b.deliveryDate) : new Date(0);
          break;
        case 'carrier':
          aValue = a.carrier;
          bValue = b.carrier;
          break;
        case 'originLocation':
          aValue = a.originLocation;
          bValue = b.originLocation;
          break;
        case 'pallets':
          aValue = a.pallets;
          bValue = b.pallets;
          break;
        case 'destination':
          aValue = a.destination;
          bValue = b.destination;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const palletDeliveries = getSortedPalletDeliveries();

  // Format number with commas
  const formatNumber = num => {
    return num?.toLocaleString() || '0';
  };

  // Prepare carrier chart data - ALL carriers, no limits
  const carrierChartData = useMemo(() => {
    if (statistics.allCarriers.length === 0) {
      return { series: [], categories: [] };
    }

    // Sort by pallets descending (all carriers)
    const sortedCarriers = [...statistics.allCarriers].sort(
      (a, b) => b.pallets - a.pallets
    );

    return {
      series: [
        {
          name: 'Pallets',
          data: sortedCarriers.map(item => item.pallets)
        }
      ],
      categories: sortedCarriers.map(item => {
        // Truncate long carrier names for display but keep all carriers
        const name = item.carrier;
        return name.length > 25 ? name.substring(0, 23) + '...' : name;
      })
    };
  }, [statistics.allCarriers]);

  // Prepare location chart data - ALL locations, no limits
  const locationChartData = useMemo(() => {
    if (statistics.allLocations.length === 0) {
      return { series: [], categories: [] };
    }

    // Sort by pallets descending (all locations)
    const sortedLocations = [...statistics.allLocations]
      .slice(0, 10)
      .sort((a, b) => b.pallets - a.pallets);

    return {
      series: [
        {
          name: 'Pallets',
          data: sortedLocations.map(item => item.pallets)
        }
      ],
      categories: sortedLocations.map(item => {
        // Truncate long location names for display but keep all locations
        const name = item.location;
        return name.length > 25 ? name.substring(0, 23) + '...' : name;
      })
    };
  }, [statistics.allLocations]);

  return (
    <Box sx={{ p: 1 }}>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Inventory sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Total Pallets Delivered
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {formatNumber(statistics.totalPallets)}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Across {statistics.totalShipments} shipments
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocalShipping sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Unique Carriers
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.uniqueCarriers}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Active carriers
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Avg Pallets/Shipment
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.avgPalletsPerShipment}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Per delivery
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Active Locations
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.allLocations.length}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Shipping origins
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* All Carriers by Pallets Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocalShipping color='success' />
                  All Carriers by Pallets
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart && setExpandedChart('allCarriersPallets')
                    }
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'success.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {carrierChartData.categories.length > 0 ? (
                  <Box sx={{ minWidth: 400, height: 300 }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: { position: 'right' }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: val => formatNumber(val),
                          offsetX: 30,
                          style: {
                            fontSize: '11px',
                            fontWeight: 'bold',
                            colors: ['#000']
                          }
                        },
                        xaxis: {
                          categories: carrierChartData.categories,
                          labels: {
                            formatter: val => formatNumber(val)
                          }
                        },
                        yaxis: {
                          labels: {
                            style: {
                              fontSize: '11px',
                              fontWeight: 500
                            },
                            formatter: (val, index) => {
                              return carrierChartData.categories[index] || val;
                            }
                          }
                        },
                        colors: ['#00E396'],
                        tooltip: {
                          y: {
                            formatter: (val, { dataPointIndex }) => {
                              const fullCarrierName =
                                statistics.allCarriers[dataPointIndex]
                                  ?.carrier || '';
                              return `${formatNumber(val)} pallets (${fullCarrierName})`;
                            }
                          }
                        },
                        grid: {
                          padding: {
                            left: 20,
                            right: 40
                          }
                        }
                      }}
                      series={carrierChartData.series}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No carrier data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* All Locations by Pallets Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocationOn color='warning' />
                  Top 10 Locations by Pallets
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart &&
                      setExpandedChart('allLocationsPallets')
                    }
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'warning.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {locationChartData.categories.length > 0 ? (
                  <Box sx={{ minWidth: 400, height: 300 }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: { position: 'right' }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: val => formatNumber(val),
                          offsetX: 30,
                          style: {
                            fontSize: '11px',
                            fontWeight: 'bold',
                            colors: ['#000']
                          }
                        },
                        xaxis: {
                          categories: locationChartData.categories,
                          labels: {
                            formatter: val => formatNumber(val)
                          }
                        },
                        yaxis: {
                          labels: {
                            style: {
                              fontSize: '11px',
                              fontWeight: 500
                            },
                            formatter: (val, index) => {
                              return locationChartData.categories[index] || val;
                            }
                          }
                        },
                        colors: ['#FEB019'],
                        tooltip: {
                          y: {
                            formatter: (val, { dataPointIndex }) => {
                              const fullLocationName =
                                statistics.allLocations[dataPointIndex]
                                  ?.location || '';
                              const shipments =
                                statistics.allLocations[dataPointIndex]
                                  ?.shipments || 0;
                              return `${formatNumber(val)} pallets from ${fullLocationName} (${shipments} shipments)`;
                            }
                          }
                        },
                        grid: {
                          padding: {
                            left: 20,
                            right: 40
                          }
                        }
                      }}
                      series={locationChartData.series}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No location data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Row 1: Daily Pallet Deliveries Chart - Full Width with Scroll */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CalendarToday color='primary' />
                  Daily Pallet Deliveries (All Time)
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      setExpandedChart &&
                      setExpandedChart('dailyPalletDeliveries')
                    }
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <ScrollableChartBox>
                {statistics.dailyChartData.dates.length > 0 ? (
                  <Box
                    sx={{
                      minWidth: statistics.dailyChartData.dates.length * 40,
                      height: 300
                    }}
                  >
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: {
                            show: true,
                            tools: {
                              download: true,
                              selection: true,
                              zoom: true,
                              pan: true
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            borderRadius: 4,
                            columnWidth: '60%',
                            dataLabels: { position: 'top' }
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        xaxis: {
                          categories: statistics.dailyChartData.dates,
                          labels: {
                            rotate: -45,
                            rotateAlways: false,
                            style: { fontSize: '10px' }
                          },
                          tickAmount: 20
                        },
                        yaxis: {
                          title: {
                            text: 'Number of Pallets'
                          },
                          labels: {
                            formatter: val => Math.round(val)
                          }
                        },
                        colors: ['#008FFB'],
                        tooltip: {
                          y: {
                            formatter: val => val + ' pallets'
                          }
                        },
                        grid: {
                          padding: { left: 10, right: 10 }
                        },
                        title: {
                          text: `Total Days: ${statistics.dailyChartData.dates.length}`,
                          align: 'left',
                          style: { fontSize: '12px' }
                        }
                      }}
                      series={[
                        {
                          name: 'Pallets Delivered',
                          data: statistics.dailyChartData.pallets
                        }
                      ]}
                      type='bar'
                      height={280}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    <Typography color='text.secondary'>
                      No daily data available
                    </Typography>
                  </Box>
                )}
              </ScrollableChartBox>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Row 2: Carrier and Location Charts side by side with scroll */}

      {/* Row 4: Detailed Pallet Deliveries Table */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Detailed Pallet Deliveries (All Time)
              </Typography>
              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'deliveryDate'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('deliveryDate')}
                        >
                          Delivery Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'carrier'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('carrier')}
                        >
                          Carrier
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'originLocation'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('originLocation')}
                        >
                          Origin Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'destination'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('destination')}
                        >
                          Destination
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align='right'>
                        <TableSortLabel
                          active={sortConfig.field === 'pallets'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('pallets')}
                        >
                          Pallets
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>PRO #</TableCell>
                      <TableCell>BOL #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {palletDeliveries.map(delivery => (
                      <TableRow key={delivery.id} hover>
                        <TableCell>
                          {delivery.deliveryDate
                            ? moment(delivery.deliveryDate).format(
                                'MMM DD, YYYY'
                              )
                            : '-'}
                        </TableCell>
                        <TableCell>{delivery.carrier}</TableCell>
                        <TableCell>{delivery.originLocation}</TableCell>
                        <TableCell>{delivery.destination}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold' }}>
                          {delivery.pallets}
                        </TableCell>
                        <TableCell>{delivery.proNumber}</TableCell>
                        <TableCell>{delivery.bolNumber}</TableCell>
                      </TableRow>
                    ))}
                    {palletDeliveries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align='center' sx={{ py: 3 }}>
                          <Alert severity='info'>
                            No pallet delivery data available
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {palletDeliveries.length > 0 && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  Total Deliveries: {palletDeliveries.length}
                </Typography>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PalletScoreCard;
